import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownToLine,
  Check,
  ChevronDown,
  ChevronRight,
  CircleSlash,
  Download,
  Eye,
  EyeOff,
  HelpCircle,
  Link2,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import type {
  Olt,
  Page,
  SmartoltMigration,
  SmartoltField,
  SmartoltFieldChange,
  SmartoltImport,
  SmartoltImportSummary,
  SmartoltImportTarget,
  SmartoltProbe,
  SmartoltSettings,
} from '@simpleacs/shared';
import {
  parseSmartoltSubdomain,
  smartoltBaseUrlFor,
  SMARTOLT_FIELDS,
  SMARTOLT_FIELD_LABELS,
} from '@simpleacs/shared';
import { api } from '../lib/api';
import { MigrateDialog } from './smartolt/MigrateDialog';
import { MigrationsCard } from './smartolt/MigrationsCard';
import { formatRelative, formatTimestamp } from '../lib/format';
import { useSession } from '../lib/session';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  Pill,
  Spinner,
  StatCard,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

/**
 * Import from SmartOLT.
 *
 * The screen is the review, not the import. SmartOLT is read, matched against
 * what this platform holds, and shown field by field — what SmartOLT has, what
 * SimpleACS has, and what would change — and nothing lands until the operator
 * accepts THAT preview by typing its number back.
 *
 * The rule stated on the page because it is stated in the code: a field
 * SimpleACS already holds is never overwritten. It is reported as a conflict
 * and skipped, and the operator decides by hand on the one ONU where it
 * matters. This exists because the C650 masks every subscriber name, so
 * SmartOLT is the only place some of them survive — and an importer that could
 * overwrite would be able to undo months of the operator's own corrections in
 * one press.
 */

const DECISION_TONE: Record<SmartoltFieldChange['decision'], 'success' | 'warn' | 'neutral' | 'info'> = {
  fill: 'success',
  conflict: 'warn',
  same: 'neutral',
  unresolved: 'info',
  ignored: 'neutral',
};

const DECISION_LABEL: Record<SmartoltFieldChange['decision'], string> = {
  fill: 'would fill',
  conflict: 'conflict — kept as ours',
  same: 'already agrees',
  unresolved: 'cannot resolve',
  ignored: 'not imported',
};

/**
 * A TAB on Settings, not a page of its own (operator's call, 2026-08-25) — so
 * it renders no `PageHeader`; Settings already carries one. Importing is a
 * thing you set up once beside the platform's other values, not a destination
 * that earns a permanent place in the nav.
 */
export function SmartOltImportTab() {
  const { session, can } = useSession();
  const isSuperadmin = session?.user.role === 'superadmin';
  const [preview, setPreview] = useState<SmartoltImport | null>(null);
  /**
   * The migration wizard: `'new'` opens it at Connect, a migration re-opens
   * its review (resumable). The one primary action on this tab, and at the
   * top, because "bring the whole customer across" is what somebody arrives
   * here to do; the field-by-field import below is the finer tool.
   */
  const [migrate, setMigrate] = useState<'new' | SmartoltMigration | null>(null);

  return (
    <div className="grid gap-5">
      {can('olt.manage') && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-surface px-5 py-4">
          <div>
            <p className="text-sm font-medium text-ink">Migrate a customer from SmartOLT</p>
            <p className="text-xs text-muted">
              Their URL and API key, a choice of what to bring, one frozen review per group, then
              the tray does the writing. Nothing here is overwritten and no chassis is touched.
            </p>
          </div>
          <Button
            variant="primary"
            icon={<ArrowDownToLine className="size-4" />}
            onClick={() => setMigrate('new')}
          >
            Migrate from SmartOLT
          </Button>
        </div>
      )}
      {isSuperadmin && <ConnectionCard />}
      <RulesCard />
      {can('olt.manage') && <PreviewBuilder onPreview={setPreview} />}
      {preview && <PreviewPanel preview={preview} onChange={setPreview} />}
      <HistoryCard onOpen={setPreview} />
      <MigrationsCard onOpen={setMigrate} />
      {migrate && (
        <MigrateDialog
          existing={migrate === 'new' ? null : migrate}
          onClose={() => setMigrate(null)}
        />
      )}
    </div>
  );
}

/**
 * The merge rules, on the page rather than in a changelog.
 *
 * An operator about to point this at 4,657 live subscribers is entitled to
 * read what it will and will not do BEFORE it does it, in the same words the
 * code uses.
 */
function RulesCard() {
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <ShieldAlert className="size-4 text-muted" />
          What an import will and will not do
        </span>
      }
    >
      <ul className="grid gap-2 px-5 py-4 text-sm text-muted">
        <li>
          <span className="font-medium text-ink">It only ever fills empty fields.</span> A field
          SimpleACS already holds is reported as a conflict and left exactly as it is — both values
          are shown so you can decide by hand on the one ONU where it matters.
        </li>
        <li>
          <span className="font-medium text-ink">It matches on the serial</span>, ignoring case,
          spaces and punctuation. Nothing else identifies an ONU across two systems.
        </li>
        <li>
          <span className="font-medium text-ink">It creates nothing.</span> An ONU SmartOLT has and
          this platform does not is listed, not invented. A zone or ODB that does not exist here is
          reported as unresolved — create it first, then preview again.
        </li>
        <li>
          <span className="font-medium text-ink">
            An external ID is an address, and is treated as one.
          </span>{' '}
          SmartOLT's default external ID is the ONU's serial, so only a renamed one is stored — that
          is the id somebody's billing system already sends. It is stored exactly as written, never
          taken from an ONU SmartOLT returned twice, and if two ONUs would end up answering to the
          same id, <span className="font-medium text-ink">neither</span> is filled in and both are
          named. The rest of the import is unaffected.
        </li>
        <li>
          <span className="font-medium text-ink">It never touches a chassis.</span> Every write goes
          to this platform's own database.
        </li>
        <li>
          <span className="font-medium text-ink">It runs once.</span> A preview is applied a single
          time, with its count typed back, and every field is re-checked against the ONU as it
          stands at that instant.
        </li>
      </ul>
    </Card>
  );
}

// --- The connection ------------------------------------------------------------

function ConnectionCard() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['smartolt', 'settings'],
    queryFn: () => api.get<SmartoltSettings>('/smartolt/settings'),
  });
  const [form, setForm] = useState<Record<string, string>>({});
  const [unmasked, setUnmasked] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [probe, setProbe] = useState<SmartoltProbe | null>(null);
  /**
   * Advanced starts OPEN for a connection that is already using an override.
   *
   * Somebody who configured a full base URL before subdomains existed must be
   * able to see what their connection actually points at without hunting for a
   * disclosure triangle — the alternative is a screen that shows an empty
   * Subdomain field and looks unconfigured.
   */
  const [advanced, setAdvanced] = useState(false);

  const seeded = useRef(false);
  useEffect(() => {
    if (!query.data || seeded.current) return;
    seeded.current = true;
    const d = query.data;
    setForm({
      subdomain: d.subdomain,
      baseUrlOverride: d.baseUrlOverride,
      token: d.token,
      onusPath: d.onusPath,
      pageSize: String(d.pageSize),
      applyCap: String(d.applyCap),
    });
    if (d.baseUrlSource === 'override') setAdvanced(true);
  }, [query.data]);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.put<SmartoltSettings>('/smartolt/settings', body),
    onSuccess: async () => {
      setError(null);
      setNotice('Saved.');
      setTimeout(() => setNotice(null), 4000);
      await queryClient.invalidateQueries({ queryKey: ['smartolt'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save'),
  });

  const test = useMutation({
    mutationFn: () => api.post<SmartoltProbe>('/smartolt/test', {}),
    onSuccess: (result) => {
      setProbe(result);
      setError(null);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not reach SmartOLT'),
  });

  const s = query.data;
  if (query.isLoading) return <Card title="SmartOLT connection"><div className="px-5 py-8"><Spinner /></div></Card>;
  if (!s) return null;
  const set = (k: string) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  /**
   * What the typed subdomain will actually read, shown as it is typed.
   *
   * Derived in the browser with the SAME function the server derives it with,
   * so the address on screen is the address the import uses — not a hint that
   * agrees with it by coincidence.
   */
  const typedSubdomain = parseSmartoltSubdomain(form.subdomain ?? '');
  const derivedUrl = typedSubdomain ? smartoltBaseUrlFor(typedSubdomain) : null;
  const usingOverride = !form.subdomain?.trim() && Boolean(form.baseUrlOverride?.trim());

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Link2 className="size-4 text-muted" />
          SmartOLT connection
        </span>
      }
      action={
        <div className="flex items-center gap-2">
          {/* What this connection actually reads, and which of the two fields
              decided it — so an operator never has to infer it from a 404. */}
          {s.baseUrl && (
            <span className="font-mono text-xs text-faint" title={`from the ${s.baseUrlSource}`}>
              {s.baseUrl}
            </span>
          )}
          <Pill state tone={s.configured ? 'success' : 'neutral'}>
            {s.configured ? 'configured' : 'not configured'}
          </Pill>
          <Button
            size="sm"
            variant="ghost"
            icon={unmasked ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
            onClick={() => setUnmasked((v) => !v)}
          >
            {unmasked ? 'Mask token' : 'Show token'}
          </Button>
        </div>
      }
    >
      <p className="border-b border-line px-5 py-3 text-sm text-muted">
        Two things: which SmartOLT, and the read API key. Everything else is{' '}
        <span className="font-medium text-ink">derived</span> — every SmartOLT instance is{' '}
        <span className="font-mono text-ink">https://&lt;subdomain&gt;.smartolt.com</span> and
        authenticates with one <span className="font-mono text-ink">X-Token</span> header, which is
        what its own API collection does on all 107 of its endpoints. The token is stored encrypted
        and never leaves this screen.
      </p>

      <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
        <Field
          label="SmartOLT subdomain"
          hint={
            derivedUrl
              ? `Reads ${derivedUrl}`
              : 'The part before .smartolt.com — if you sign in at https://acme.smartolt.com, it is “acme”.'
          }
        >
          <div className="flex items-center gap-1.5">
            <Input
              value={form.subdomain ?? ''}
              onChange={set('subdomain')}
              placeholder="acme"
              className="font-mono"
            />
            <span className="whitespace-nowrap font-mono text-xs text-faint">.smartolt.com</span>
          </div>
        </Field>
        <Field label="Read API key" hint="Stored encrypted; shown here so the field survives a refresh.">
          <Input
            type={unmasked ? 'text' : 'password'}
            name="so-token"
            value={form.token ?? ''}
            onChange={set('token')}
            placeholder="hidden — type to replace"
            className="font-mono"
            autoComplete="new-password"
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
          />
        </Field>
        <Field
          label="Apply ceiling"
          hint="The most ONUs one apply may write. Enforced on the server, after the review."
        >
          <Input type="number" value={form.applyCap ?? ''} onChange={set('applyCap')} />
        </Field>
      </div>

      {/**
       * Everything a normal SmartOLT account never needs to see.
       *
       * The full base URL lives here rather than in the main flow because it
       * exists for self-hosted or proxied instances only — and because it is
       * also where a connection configured BEFORE subdomains existed still
       * lives, unchanged and still working. The two are separate settings, so
       * an old base URL is never re-read as a subdomain.
       */}
      <div className="border-t border-line px-5 py-3">
        <button
          type="button"
          onClick={() => setAdvanced((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-medium text-muted hover:text-ink"
        >
          {advanced ? (
            <ChevronDown className="size-3.5" />
          ) : (
            <ChevronRight className="size-3.5" />
          )}
          Advanced — self-hosted instance, list path, page size
        </button>
        {advanced && (
          <div className="grid gap-4 pt-4 sm:grid-cols-2">
            <Field
              label="Full base URL"
              hint={
                usingOverride
                  ? 'IN USE. Clear it to go back to the subdomain above.'
                  : form.subdomain
                    ? 'Not in use: the subdomain above wins while it is set.'
                    : 'Only for a self-hosted or proxied instance. No path, no query string, no credentials.'
              }
            >
              <Input
                value={form.baseUrlOverride ?? ''}
                onChange={set('baseUrlOverride')}
                placeholder="https://smartolt.example.com"
                className="font-mono"
              />
            </Field>
            <Field label="ONU list path" hint="SmartOLT's own: /api/onu/get_all_onus_details.">
              <Input value={form.onusPath ?? ''} onChange={set('onusPath')} className="font-mono" />
            </Field>
            <Field
              label="Page size"
              hint="Asked for; ignored harmlessly if the API does not paginate."
            >
              <Input type="number" value={form.pageSize ?? ''} onChange={set('pageSize')} />
            </Field>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">
        <Button
          size="sm"
          variant="secondary"
          loading={test.isPending}
          disabled={!s.configured}
          onClick={() => test.mutate()}
        >
          Test connection
        </Button>
        <Button
          size="sm"
          variant="primary"
          loading={save.isPending}
          onClick={() =>
            save.mutate({
              subdomain: form.subdomain ?? '',
              baseUrlOverride: form.baseUrlOverride ?? '',
              // An empty token means "leave the stored one alone".
              ...(form.token ? { token: form.token } : {}),
              onusPath: form.onusPath,
              pageSize: Number(form.pageSize) || undefined,
              applyCap: Number(form.applyCap) || undefined,
            })
          }
        >
          Save connection
        </Button>
      </div>

      {error && <p className="px-5 py-3 text-sm text-danger">{error}</p>}
      {notice && <p className="px-5 py-3 text-sm text-success">{notice}</p>}
      {probe && <ProbeResult probe={probe} />}
    </Card>
  );
}

/**
 * What the probe found — including the FIELD NAMES SmartOLT actually used.
 *
 * That list is the most useful thing on this page for the first real
 * connection: the adapter guesses where a name or a zone lives, and this is
 * how the guess gets corrected against the real instance.
 */
function ProbeResult({ probe }: { probe: SmartoltProbe }) {
  return (
    <div className="border-t border-line px-5 py-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Pill state tone={probe.ok ? 'success' : 'danger'} dot>
          {probe.ok ? `HTTP ${probe.status}` : 'no answer'}
        </Pill>
        <span className="text-xs text-faint">{probe.latencyMs} ms</span>
        {probe.ok && (
          <span className="text-xs text-faint">
            {probe.recordCount} record{probe.recordCount === 1 ? '' : 's'} on the first page
            {probe.totalReported !== null ? ` · ${probe.totalReported} reported in total` : ''}
          </span>
        )}
      </div>
      {probe.error && <p className="text-sm text-danger">{probe.error}</p>}
      {probe.warnings.map((w) => (
        <p key={w} className="text-sm text-warn">
          {w}
        </p>
      ))}
      {probe.ok && (
        <div className="grid gap-3 pt-2 sm:grid-cols-2">
          <div>
            <p className="label">Fields SmartOLT returned</p>
            <p className="font-mono text-xs text-muted">{probe.fieldsSeen.join(', ') || '—'}</p>
          </div>
          <div>
            <p className="label">Of the first page, how many carried</p>
            <p className="text-xs text-muted">
              {SMARTOLT_FIELDS.map(
                (f) => `${SMARTOLT_FIELD_LABELS[f]}: ${probe.fieldCoverage[f] ?? 0}`,
              ).join(' · ')}
            </p>
          </div>
          {probe.sampleSerials.length > 0 && (
            <div>
              <p className="label">Serials seen</p>
              <p className="font-mono text-xs text-muted">{probe.sampleSerials.join(', ')}</p>
            </div>
          )}
        </div>
      )}
      <p className="mt-3 text-xs text-faint">
        Nothing was imported and nothing was stored by this test.
      </p>
    </div>
  );
}

// --- Taking a preview ------------------------------------------------------------

function PreviewBuilder({ onPreview }: { onPreview: (p: SmartoltImport) => void }) {
  const queryClient = useQueryClient();
  const [fields, setFields] = useState<SmartoltField[]>([...SMARTOLT_FIELDS]);
  const [oltId, setOltId] = useState('');
  const [serials, setSerials] = useState('');
  const [error, setError] = useState<string | null>(null);

  const olts = useQuery({
    queryKey: ['olts', 'smartolt'],
    queryFn: () => api.get<Page<Olt>>('/olts'),
  });

  const take = useMutation({
    mutationFn: () =>
      api.post<SmartoltImport>('/smartolt/imports', {
        fields,
        oltId: oltId || null,
        serials:
          serials.trim() === ''
            ? null
            : serials
                .split(/[\s,]+/)
                .map((s) => s.trim())
                .filter(Boolean),
      }),
    onSuccess: async (result) => {
      setError(null);
      onPreview(result);
      await queryClient.invalidateQueries({ queryKey: ['smartolt', 'imports'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not read SmartOLT'),
  });

  const toggle = (field: SmartoltField) =>
    setFields((current) =>
      current.includes(field) ? current.filter((f) => f !== field) : [...current, field],
    );

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Download className="size-4 text-muted" />
          Take a preview
        </span>
      }
    >
      <div className="grid gap-4 px-5 py-4">
        <Field label="Bring across" hint="Anything not ticked is reported but never written.">
          <div className="flex flex-wrap gap-2">
            {SMARTOLT_FIELDS.map((field) => (
              <button
                key={field}
                type="button"
                onClick={() => toggle(field)}
                className={
                  fields.includes(field)
                    ? 'rounded-lg border border-primary bg-primary-soft px-3 py-1.5 text-sm font-medium text-primary'
                    : 'rounded-lg border border-line px-3 py-1.5 text-sm text-muted hover:text-ink'
                }
              >
                {SMARTOLT_FIELD_LABELS[field]}
              </button>
            ))}
          </div>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Narrow to one chassis" hint="Optional. The first real import should use it.">
            <select className="input" value={oltId} onChange={(e) => setOltId(e.target.value)}>
              <option value="">Every OLT in scope</option>
              {(olts.data?.items ?? []).map((olt) => (
                <option key={olt.id} value={olt.id}>
                  {olt.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="…or just these serials"
            hint="One per line, or separated by commas. The safest possible first import."
          >
            <textarea
              className="input min-h-[38px]"
              rows={2}
              value={serials}
              onChange={(e) => setSerials(e.target.value)}
              placeholder="ZTEGC6A2955E"
            />
          </Field>
        </div>

        {error && <ErrorNotice error={new Error(error)} />}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-line px-5 py-3">
        <p className="text-xs text-faint">
          Reads SmartOLT and writes nothing. You will see every change before any of it lands.
        </p>
        <Button
          size="sm"
          variant="primary"
          loading={take.isPending}
          disabled={fields.length === 0}
          onClick={() => take.mutate()}
        >
          Take preview
        </Button>
      </div>
    </Card>
  );
}

// --- Reading a preview -------------------------------------------------------------

function PreviewPanel({
  preview,
  onChange,
}: {
  preview: SmartoltImport;
  onChange: (p: SmartoltImport | null) => void;
}) {
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'fill' | 'conflict' | 'unresolved' | 'unknown_here' | 'missing_there'>(
    'fill',
  );

  useEffect(() => {
    setConfirm('');
    setError(null);
    setTab('fill');
  }, [preview.id]);

  const apply = useMutation({
    mutationFn: () =>
      api.post<SmartoltImport>(`/smartolt/imports/${preview.id}/apply`, {
        confirmCount: Number(confirm),
      }),
    onSuccess: async (result) => {
      setError(null);
      onChange(result);
      await queryClient.invalidateQueries({ queryKey: ['smartolt', 'imports'] });
      await queryClient.invalidateQueries({ queryKey: ['onus'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not apply'),
  });

  const discard = useMutation({
    mutationFn: () => api.delete(`/smartolt/imports/${preview.id}`),
    onSuccess: async () => {
      onChange(null);
      await queryClient.invalidateQueries({ queryKey: ['smartolt', 'imports'] });
    },
  });

  const groups = useMemo(() => split(preview.targets), [preview.targets]);
  const applied = preview.status === 'applied';
  const rows = groups[tab];

  const TABS: Array<{ id: typeof tab; label: string; count: number; tone: 'success' | 'warn' | 'info' | 'neutral' }> = [
    { id: 'fill', label: applied ? 'Filled in' : 'Would be filled', count: groups.fill.length, tone: 'success' },
    { id: 'conflict', label: 'Conflicts (kept as ours)', count: groups.conflict.length, tone: 'warn' },
    { id: 'unresolved', label: 'Cannot resolve', count: groups.unresolved.length, tone: 'info' },
    { id: 'unknown_here', label: 'In SmartOLT, unknown here', count: groups.unknown_here.length, tone: 'neutral' },
    { id: 'missing_there', label: 'Here, absent from SmartOLT', count: groups.missing_there.length, tone: 'neutral' },
  ];

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <ArrowDownToLine className="size-4 text-muted" />
          {applied ? 'Applied import' : 'Preview'} · {preview.baseUrl}
        </span>
      }
      action={
        <div className="flex items-center gap-2">
          <Pill state tone={applied ? 'success' : preview.overCap ? 'danger' : 'primary'}>
            {preview.status}
          </Pill>
          {!applied && (
            <Button
              size="sm"
              variant="ghost"
              icon={<Trash2 className="size-3.5" />}
              loading={discard.isPending}
              onClick={() => discard.mutate()}
            >
              Discard
            </Button>
          )}
        </div>
      }
    >
      <div className="grid gap-3 border-b border-line px-5 py-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="In SmartOLT" value={preview.sourceCount} hint="records returned" />
        <StatCard label="Matched here" value={preview.matchedCount} hint="by serial" />
        <StatCard
          label={applied ? 'ONUs filled' : 'ONUs to fill'}
          value={applied ? preview.appliedCount : preview.fillableCount}
          tone="success"
          hint={`${preview.fieldCount} field${preview.fieldCount === 1 ? '' : 's'}`}
        />
        <StatCard
          label="Conflicts"
          value={preview.conflictCount}
          tone={preview.conflictCount > 0 ? 'warn' : 'default'}
          hint="left exactly as SimpleACS holds them"
        />
      </div>

      {preview.note && (
        <p className="border-b border-line bg-warn-soft/40 px-5 py-3 text-sm text-warn">{preview.note}</p>
      )}
      {preview.overCap && !applied && (
        <p className="border-b border-line bg-danger-soft/40 px-5 py-3 text-sm text-danger">
          {preview.fillableCount} ONUs is over the ceiling of {preview.cap}. Narrow to one chassis or
          a list of serials, or raise the ceiling on the connection above — the server refuses this
          either way.
        </p>
      )}

      <div className="flex gap-1 overflow-x-auto border-b border-line px-3">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            onClick={() => setTab(entry.id)}
            className={
              tab === entry.id
                ? 'whitespace-nowrap border-b-2 border-primary px-3 py-2.5 text-sm font-medium text-primary'
                : 'whitespace-nowrap border-b-2 border-transparent px-3 py-2.5 text-sm text-muted hover:text-ink'
            }
          >
            {entry.label}{' '}
            <span className="ml-1 rounded-full bg-surface-2 px-1.5 text-xs">{entry.count}</span>
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon={<CircleSlash className="size-6" />}
          title="Nothing in this group"
          description={
            tab === 'fill'
              ? 'Every matched field is either already held here, already agrees, or could not be resolved.'
              : undefined
          }
        />
      ) : (
        <TargetTable rows={rows} showChanges={tab !== 'unknown_here' && tab !== 'missing_there'} />
      )}

      {!applied && (
        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-line px-5 py-4">
          {error && <p className="mr-auto text-sm text-danger">{error}</p>}
          <span className="text-sm text-muted">
            Type <span className="font-mono font-semibold text-ink">{preview.fillableCount}</span> to
            confirm
          </span>
          <Input
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-24 text-center font-mono"
            placeholder="0"
          />
          <Button
            size="sm"
            variant="primary"
            icon={<Check className="size-3.5" />}
            loading={apply.isPending}
            disabled={preview.fillableCount === 0 || Number(confirm) !== preview.fillableCount}
            onClick={() => apply.mutate()}
          >
            Fill in {preview.fillableCount} ONU{preview.fillableCount === 1 ? '' : 's'}
          </Button>
        </div>
      )}
      {applied && (
        <p className="border-t border-line px-5 py-3 text-sm text-muted">
          Applied {formatRelative(preview.appliedAt ?? preview.createdAt)} by {preview.appliedBy}.{' '}
          {preview.appliedCount} filled, {preview.skippedCount} skipped, {preview.failedCount} failed.
          An applied import runs once and is kept as the record of what changed.
        </p>
      )}
    </Card>
  );
}

function TargetTable({ rows, showChanges }: { rows: SmartoltImportTarget[]; showChanges: boolean }) {
  return (
    <Table>
      <THead>
        <th className="px-5 py-2.5 text-left text-xs font-medium text-muted">Serial</th>
        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted">OLT</th>
        {showChanges ? (
          <th className="px-3 py-2.5 text-left text-xs font-medium text-muted">Field by field</th>
        ) : (
          <th className="px-3 py-2.5 text-left text-xs font-medium text-muted">What this means</th>
        )}
        <th className="px-5 py-2.5 text-left text-xs font-medium text-muted">Outcome</th>
      </THead>
      <TBody>
        {rows.map((row) => (
          <TRow key={`${row.serialKey}-${row.onuId ?? 'none'}`}>
            <td className="px-5 py-3 align-top">
              <p className="font-mono text-xs text-ink">{row.serial}</p>
              {row.name && <p className="text-xs text-faint">{row.name}</p>}
            </td>
            <td className="px-3 py-3 align-top text-xs text-muted">
              {row.oltName ?? '—'}
              {row.interfaceName && (
                <p className="font-mono text-[11px] text-faint">{row.interfaceName}</p>
              )}
            </td>
            <td className="px-3 py-3 align-top">
              {showChanges ? (
                <div className="grid gap-1.5">
                  {row.changes
                    .filter((c) => c.decision !== 'ignored')
                    .map((change) => (
                      <ChangeLine key={change.field} change={change} />
                    ))}
                </div>
              ) : (
                <p className="text-xs text-muted">{row.message}</p>
              )}
            </td>
            <td className="px-5 py-3 align-top">
              {row.outcome ? (
                <Pill state
                  tone={
                    row.outcome === 'ok'
                      ? 'success'
                      : row.outcome === 'failed'
                        ? 'danger'
                        : row.outcome === 'conflict'
                          ? 'warn'
                          : 'neutral'
                  }
                >
                  {row.outcome}
                </Pill>
              ) : (
                <span className="text-xs text-faint">—</span>
              )}
              {row.outcome && row.message && (
                <p className="mt-1 max-w-xs text-[11px] text-faint">{row.message}</p>
              )}
            </td>
          </TRow>
        ))}
      </TBody>
    </Table>
  );
}

/** One field: what SmartOLT holds, what we hold, and which of them wins (never theirs). */
function ChangeLine({ change }: { change: SmartoltFieldChange }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="min-w-28 font-medium text-ink">{SMARTOLT_FIELD_LABELS[change.field]}</span>
      <Pill state tone={DECISION_TONE[change.decision]}>{DECISION_LABEL[change.decision]}</Pill>
      <span className="text-muted">
        SmartOLT: <span className="text-ink">{change.remote ?? '—'}</span>
      </span>
      <span className="text-muted">
        here: <span className="text-ink">{change.local ?? '—'}</span>
      </span>
      <span className="basis-full text-[11px] text-faint" title={change.reason}>
        {change.reason}
      </span>
    </div>
  );
}

function split(targets: SmartoltImportTarget[]): Record<
  'fill' | 'conflict' | 'unresolved' | 'unknown_here' | 'missing_there',
  SmartoltImportTarget[]
> {
  const out = {
    fill: [] as SmartoltImportTarget[],
    conflict: [] as SmartoltImportTarget[],
    unresolved: [] as SmartoltImportTarget[],
    unknown_here: [] as SmartoltImportTarget[],
    missing_there: [] as SmartoltImportTarget[],
  };
  for (const target of targets) {
    if (target.kind === 'unknown_here') {
      out.unknown_here.push(target);
      continue;
    }
    if (target.kind === 'missing_there') {
      out.missing_there.push(target);
      continue;
    }
    // A matched ONU can appear in more than one group — one field filled and
    // another in conflict is the ordinary case, and hiding either half would
    // be the misleading half.
    if (target.changes.some((c) => c.decision === 'fill')) out.fill.push(target);
    if (target.changes.some((c) => c.decision === 'conflict')) out.conflict.push(target);
    if (target.changes.some((c) => c.decision === 'unresolved')) out.unresolved.push(target);
  }
  return out;
}

// --- History ---------------------------------------------------------------------

function HistoryCard({ onOpen }: { onOpen: (p: SmartoltImport) => void }) {
  const query = useQuery({
    queryKey: ['smartolt', 'imports'],
    queryFn: () => api.get<Page<SmartoltImportSummary>>('/smartolt/imports'),
  });

  const open = useMutation({
    mutationFn: (id: string) => api.get<SmartoltImport>(`/smartolt/imports/${id}`),
    onSuccess: onOpen,
  });

  const items = query.data?.items ?? [];

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <HelpCircle className="size-4 text-muted" />
          Every import, applied or not
        </span>
      }
    >
      {query.isLoading ? (
        <div className="px-5 py-8">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title="No imports yet"
          description="Take a preview above. Nothing is written until you accept one."
        />
      ) : (
        <Table>
          <THead>
            <th className="px-5 py-2.5 text-left text-xs font-medium text-muted">When</th>
            <th className="px-3 py-2.5 text-left text-xs font-medium text-muted">By</th>
            <th className="px-3 py-2.5 text-left text-xs font-medium text-muted">Fields</th>
            <th className="px-3 py-2.5 text-left text-xs font-medium text-muted">Matched</th>
            <th className="px-3 py-2.5 text-left text-xs font-medium text-muted">Filled</th>
            <th className="px-3 py-2.5 text-left text-xs font-medium text-muted">Conflicts</th>
            <th className="px-5 py-2.5 text-left text-xs font-medium text-muted">Status</th>
          </THead>
          <TBody>
            {items.map((row) => (
              <TRow key={row.id} onClick={() => open.mutate(row.id)}>
                <td className="px-5 py-3 text-xs text-muted" title={formatTimestamp(row.createdAt)}>
                  {formatRelative(row.createdAt)}
                </td>
                <td className="px-3 py-3 text-xs text-muted">{row.createdBy ?? '—'}</td>
                <td className="px-3 py-3 text-xs text-muted">
                  {row.fields.map((f) => SMARTOLT_FIELD_LABELS[f]).join(', ')}
                </td>
                <td className="px-3 py-3 text-xs text-ink">{row.matchedCount}</td>
                <td className="px-3 py-3 text-xs text-ink">
                  {row.status === 'applied' ? row.appliedCount : `${row.fillableCount} pending`}
                </td>
                <td className="px-3 py-3 text-xs text-ink">{row.conflictCount}</td>
                <td className="px-5 py-3">
                  <Pill state tone={row.status === 'applied' ? 'success' : row.status === 'draft' ? 'primary' : 'neutral'}>
                    {row.status}
                  </Pill>
                </td>
              </TRow>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}
